/**
 * The tiered DEM resolver (SPEC §3.1).
 *
 * Picks the best available source for the requested area and falls through on
 * failure. This is the single seam between elevation sources and everything
 * else — adding a provider means adding a module and one line to `TIERS`, and
 * must never require touching the solver.
 */

import type { DemProvider, DemRequest, DemResult, ProviderId } from './types';
import { NoCoverageError } from './types';
import { metricDomain, type MetricDomain } from './projection';
import { cacheKey, readCache, writeCache } from './cache';
import { terrariumProvider } from './providers/terrarium';
import { usgs3depProvider } from './providers/usgs3dep';
import { swisstopoProvider } from './providers/swisstopo';

/** Highest resolution first; the resolver walks down until one succeeds. */
export const TIERS: DemProvider[] = [
  swisstopoProvider,
  usgs3depProvider,
  terrariumProvider,
];

/**
 * The global fallback. Coarse and noisy compared with the regional sources, and
 * the only one that is always available — so anything that cannot be trusted on
 * a ~30 m noisy DEM has to check which provider actually answered.
 */
export const FALLBACK_PROVIDER: ProviderId = 'terrarium';

/**
 * Attempts per provider before falling through to the next tier.
 *
 * USGS 3DEP rate-limits under load and drops its CORS header on the error
 * response when it does, which surfaces as a hard network failure rather than
 * a retryable status. A single attempt therefore dropped straight to Terrarium
 * on what was usually a transient refusal — and Terrarium is markedly worse for
 * everything downstream of terrain roughness. One retry recovers most of them.
 * The fallback itself is not retried: if it fails there is nowhere left to go.
 */
const ATTEMPTS_PER_PROVIDER = 2;
const RETRY_DELAY_MS = 450;

const delay = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });

export const ALL_PROVIDERS: DemProvider[] = [...TIERS];

export function providerById(id: ProviderId): DemProvider | undefined {
  return ALL_PROVIDERS.find((p) => p.info.id === id);
}

export interface ResolveOptions extends DemRequest {
  /** force one provider instead of walking the tiers */
  force?: ProviderId;
  /** called as the resolver falls through, for UI feedback */
  onAttempt?: (provider: ProviderId) => void;
  useCache?: boolean;
}

/**
 * Resolve elevation for `bbox` at the working grid size, returning a normalised
 * `HeightGrid` in a local metric frame.
 */
export async function resolveDEM(opts: ResolveOptions): Promise<DemResult> {
  const domain: MetricDomain = metricDomain(opts.bbox, opts.gridSize);
  const useCache = opts.useCache ?? true;

  const candidates: DemProvider[] = opts.force
    ? [providerById(opts.force)].filter((p): p is DemProvider => !!p)
    : TIERS.filter((p) => p.info.covers(domain.bbox));

  if (candidates.length === 0) candidates.push(terrariumProvider);

  const failures: string[] = [];

  for (const provider of candidates) {
    const id = provider.info.id;
    const key = cacheKey(id, domain.bbox, domain.cellSize);
    if (useCache) {
      const hit = await readCache(key);
      if (hit) {
        return {
          grid: hit,
          domain,
          provider: id,
          attribution: provider.info.attribution,
          cached: true,
        };
      }
    }

    opts.onAttempt?.(id);
    const attempts = id === FALLBACK_PROVIDER ? 1 : ATTEMPTS_PER_PROVIDER;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < attempts; attempt++) {
      if (attempt > 0) await delay(RETRY_DELAY_MS, opts.signal);
      try {
        const grid = await provider.load(opts, domain);
        if (!isPlausible(grid)) throw new NoCoverageError(id);

        if (useCache) {
          void writeCache(key, id, grid);
        }
        return {
          grid,
          domain,
          provider: id,
          attribution: provider.info.attribution,
          cached: false,
        };
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') throw err;
        // No point retrying a provider that simply does not cover this ground.
        if (err instanceof NoCoverageError) {
          lastError = err as Error;
          break;
        }
        lastError = err as Error;
      }
    }
    failures.push(`${id}: ${lastError?.message ?? 'failed'}`);
  }

  throw new Error(`No DEM provider returned data. Tried — ${failures.join('; ')}`);
}

/**
 * Reject a grid that is entirely flat or full of sentinels. A provider that
 * returns zeros outside its footprint would otherwise look like a valid ocean
 * tile and stop the fall-through.
 */
function isPlausible(grid: { data: Float32Array }): boolean {
  let min = Infinity;
  let max = -Infinity;
  let nonZero = 0;
  const step = Math.max(1, Math.floor(grid.data.length / 4096));
  for (let i = 0; i < grid.data.length; i += step) {
    const v = grid.data[i];
    if (!Number.isFinite(v) || v < -12000 || v > 9000) return false;
    if (v !== 0) nonZero++;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  // A genuinely flat sea-level grid is legitimate, but an all-zero grid from a
  // provider that should have coverage is not.
  return nonZero > 0 || max - min > 0;
}
